import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Film, Music } from 'lucide-react';
import { cn } from '@/lib/utils';
import { templateService } from '@/services/templateService';
import { audioService } from '@/services/audioService';
import { TemplateSelector } from '@/components/templates/TemplateSelector';
import { TemplatePreview } from '@/components/templates/TemplatePreview';
import { AudioCatalogBrowser } from '@/components/audio/AudioCatalogBrowser';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { VideoTemplate } from '@/types/template';
import type { AudioTrack } from '@/types/audio';
import { AdminPageShell, PageHeader, PageLoading } from '@/components/admin/shell';

type TabType = 'template' | 'audio';

const TAB_OPTIONS: { value: TabType; label: string; icon: typeof Film }[] = [
  { value: 'template', label: 'Plantilla', icon: Film },
  { value: 'audio', label: 'Música', icon: Music },
];

export function VideoSettings() {
  const [selectedTemplate, setSelectedTemplate] = useState<VideoTemplate | null>(null);
  const [selectedAudioTrack, setSelectedAudioTrack] = useState<AudioTrack | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('template');

  useEffect(() => {
    loadCurrentSettings();
  }, []);

  const loadCurrentSettings = async () => {
    setIsLoading(true);
    try {
      const [templateSelection, audioSelection] = await Promise.all([
        templateService.getSelection(),
        audioService.getSelection()
      ]);
      setSelectedTemplate(templateSelection.video_template);
      if (audioSelection.audio_track) {
        setSelectedAudioTrack(audioSelection.audio_track);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      toast.error('Error al cargar la configuración');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTemplate = async (templateId: string) => {
    setIsSaving(true);
    try {
      await templateService.setSelection({ video_template_id: templateId });
      const templates = await templateService.getTemplates();
      const template = templates.templates.find(t => t.id === templateId);
      if (template) {
        setSelectedTemplate(template);
        toast.success('Plantilla actualizada');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al actualizar la plantilla');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectAudio = async (trackId: string | null) => {
    setIsSaving(true);
    try {
      if (trackId) {
        await audioService.setSelection(trackId);
        const catalog = await audioService.browseCatalog();
        const track = catalog.tracks.find(t => t.id === trackId);
        if (track) {
          setSelectedAudioTrack(track);
          toast.success('Música de fondo actualizada');
        }
      } else {
        await audioService.clearSelection();
        setSelectedAudioTrack(null);
        toast.success('Música de fondo desactivada');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al actualizar la música');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Configuración de Video" />
          <PageLoading variant="simple" />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader
          title="Configuración de Video"
          subtitle="Personaliza la plantilla y música de los videos"
        />

        <div className="flex flex-wrap gap-2">
          {TAB_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => setActiveTab(option.value)}
                disabled={isSaving}
                className={cn(
                  'rounded-md px-4 py-2 text-sm font-light transition-all min-h-11 flex items-center gap-2',
                  activeTab === option.value
                    ? 'bg-gray-900 text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                )}
              >
                <Icon className="h-4 w-4" />
                {option.label}
              </button>
            );
          })}
        </div>

        {isSaving && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-900 rounded-full" />
            Guardando...
          </div>
        )}

        {activeTab === 'template' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-light">Plantilla de Video</CardTitle>
                <CardDescription>
                  Selecciona el estilo visual de tus videos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TemplateSelector
                  selectedTemplateId={selectedTemplate?.id}
                  onSelect={handleSelectTemplate}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-light">Vista Previa</CardTitle>
                <CardDescription>
                  Así se verá el video generado
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TemplatePreview template={selectedTemplate} />
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'audio' && (
          <div className="max-w-2xl">
            <Card>
              <CardHeader>
                <CardTitle className="font-light">Música de Fondo</CardTitle>
                <CardDescription>
                  Elige la banda sonora para los videos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AudioCatalogBrowser
                  selectedTrackId={selectedAudioTrack?.id}
                  onSelect={handleSelectAudio}
                />
              </CardContent>
            </Card>

            {selectedAudioTrack && (
              <Card className="mt-6 bg-gray-50">
                <CardContent className="pt-6">
                  <h4 className="font-medium text-sm text-gray-900 mb-2">Selección Actual</h4>
                  <p className="text-sm font-light text-gray-700">
                    <span className="font-medium">{selectedAudioTrack.title}</span>
                    {' por '}
                    <span>{selectedAudioTrack.artist}</span>
                  </p>
                  <p className="text-xs font-light text-gray-500 mt-1">
                    Esta canción sonará de fondo en los videos generados
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </AdminPageShell>
  );
}
